import { getActiveSubscriptions, clearSubscriptionCache as clearSharedCache } from './subscriptionCache';

/**
 * Document Access Control Utility
 * Handles checking document subscription and storage space access
 */

// Local cache for processed document subscription data
let documentSubscriptionCache = null;
let documentCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Helper function to format bytes to human-readable format
 */
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  if (bytes < 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Fetch user's document subscription with storage space details
 * @returns {Promise<Object|null>} - Document subscription with disk space info or null
 */
const fetchDocumentSubscription = async () => {
  try {
    // Use shared subscription cache
    const response = await getActiveSubscriptions();
    
    if (response && response.subscriptions && response.subscriptions.length > 0) {
      // Find document subscription (module name is "document" - case insensitive)
      const documentSub = response.subscriptions.find(sub => {
        const moduleName = (sub.SubscriptionPlan?.Module?.name || '').toLowerCase();
        const moduleDisplayName = (sub.SubscriptionPlan?.Module?.display_name || '').toLowerCase();
        const moduleId = sub.SubscriptionPlan?.Module?.id;
        
        return moduleName === 'document' || 
               moduleDisplayName === 'document' || 
               moduleDisplayName === 'document module' ||
               moduleId === 2; // Document module ID is 2
      });
      
      if (documentSub) {
        return {
          id: documentSub.id,
          corporate_id: documentSub.corporate_id,
          subscription_plan_id: documentSub.subscription_plan_id,
          status: documentSub.status,
          disk_space_used: documentSub.disk_space_used || 0,
          SubscriptionPlan: documentSub.SubscriptionPlan,
          disk_space: documentSub.disk_space || {
            used: documentSub.disk_space_used || 0,
            used_formatted: formatBytes(documentSub.disk_space_used || 0),
            limit: documentSub.SubscriptionPlan?.disk_space_limit || 0,
            limit_formatted: formatBytes(documentSub.SubscriptionPlan?.disk_space_limit || 0),
            available: (documentSub.SubscriptionPlan?.disk_space_limit || 0) - (documentSub.disk_space_used || 0),
            available_formatted: formatBytes((documentSub.SubscriptionPlan?.disk_space_limit || 0) - (documentSub.disk_space_used || 0)),
            is_unlimited: documentSub.SubscriptionPlan?.is_unlimited_disk_space || false,
            usage_percentage: documentSub.SubscriptionPlan?.is_unlimited_disk_space 
              ? '0.00' 
              : ((documentSub.disk_space_used || 0) / (documentSub.SubscriptionPlan?.disk_space_limit || 1) * 100).toFixed(2)
          }
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching document subscription:', error);
    return null;
  }
};

/**
 * Get document subscription with caching
 * @returns {Promise<Object|null>} - Document subscription or null
 */
export const getDocumentSubscription = async () => {
  const now = Date.now();
  
  // Return cached data if it's still valid
  if (documentSubscriptionCache && documentCacheTimestamp && (now - documentCacheTimestamp) < CACHE_DURATION) {
    return documentSubscriptionCache;
  }
  
  // Fetch fresh data
  const subscription = await fetchDocumentSubscription();
  documentSubscriptionCache = subscription;
  documentCacheTimestamp = now;
  
  return subscription;
};

/**
 * Check if user has document subscription
 * @returns {Promise<boolean>} - True if user has document subscription
 */
export const hasDocumentSubscription = async () => {
  try {
    const subscription = await getDocumentSubscription();
    return subscription !== null && subscription.status === 'active';
  } catch (error) {
    console.error('Error checking document subscription:', error);
    return false;
  }
};

/**
 * Check if user has available storage space
 * @param {number} requiredBytes - Optional: bytes required for upload (default: 0, just checks if space exists)
 * @returns {Promise<Object>} - Object with hasSpace (boolean) and details
 */
export const hasStorageSpace = async (requiredBytes = 0) => {
  try {
    const subscription = await getDocumentSubscription();
    
    if (!subscription) {
      return {
        hasSpace: false,
        reason: 'no_subscription',
        message: 'No document subscription found'
      };
    }
    
    if (subscription.status !== 'active') {
      return {
        hasSpace: false,
        reason: 'inactive_subscription',
        message: 'Document subscription is not active'
      };
    }
    
    // If unlimited, always return true
    if (subscription.disk_space?.is_unlimited || subscription.SubscriptionPlan?.is_unlimited_disk_space) {
      return {
        hasSpace: true,
        isUnlimited: true,
        available: Infinity,
        available_formatted: 'Unlimited'
      };
    }
    
    const limit = subscription.SubscriptionPlan?.disk_space_limit || subscription.disk_space?.limit || 0;
    const used = subscription.disk_space_used || subscription.disk_space?.used || 0;
    const available = limit - used;
    
    if (available < requiredBytes) {
      return {
        hasSpace: false,
        reason: 'insufficient_space',
        message: `Insufficient storage space. Required: ${formatBytes(requiredBytes)}, Available: ${formatBytes(available)}`,
        available: available,
        available_formatted: formatBytes(available),
        required: requiredBytes,
        required_formatted: formatBytes(requiredBytes)
      };
    }
    
    return {
      hasSpace: true,
      available: available,
      available_formatted: formatBytes(available),
      used: used,
      used_formatted: formatBytes(used),
      limit: limit,
      limit_formatted: formatBytes(limit),
      usage_percentage: subscription.disk_space?.usage_percentage || '0.00'
    };
  } catch (error) {
    console.error('Error checking storage space:', error);
    return {
      hasSpace: false,
      reason: 'error',
      message: 'Error checking storage space'
    };
  }
};

/**
 * Check if user can upload documents (has subscription and has space)
 * @param {number} requiredBytes - Optional: bytes required for upload
 * @returns {Promise<Object>} - Object with canUpload (boolean) and details
 */
export const canUploadDocuments = async (requiredBytes = 0) => {
  try {
    const hasSubscription = await hasDocumentSubscription();
    
    if (!hasSubscription) {
      return {
        canUpload: false,
        reason: 'no_subscription',
        message: 'Document subscription is required to upload files'
      };
    }
    
    const spaceCheck = await hasStorageSpace(requiredBytes);
    
    if (!spaceCheck.hasSpace) {
      return {
        canUpload: false,
        reason: spaceCheck.reason,
        message: spaceCheck.message,
        ...spaceCheck
      };
    }
    
    return {
      canUpload: true,
      ...spaceCheck
    };
  } catch (error) {
    console.error('Error checking upload permission:', error);
    return {
      canUpload: false,
      reason: 'error',
      message: 'Error checking upload permission'
    };
  }
};

/**
 * Clear the document subscription cache
 */
export const clearDocumentSubscriptionCache = () => {
  documentSubscriptionCache = null;
  documentCacheTimestamp = null;
  // Also clear the shared cache
  clearSharedCache();
};
